import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import BrandSelect from '../../ui/BrandSelect';
import Breadcrumb from '../../Common/Breadcrumb';
import { adminToolbarSelect } from '../../utils/ReactSelectStyles';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import ClientMixPanel from '../Partners/ClientMixPanel';
import PartnersFilterBar from '../Partners/PartnersFilterBar';
import { formatAgreementSigned, readListFilters, reasonLabel } from '../Partners/partnersListFilters';
import { Actions as ADMINACTIONS } from '../../../store/actions/adminActions';
import { Actions as COMMERCIAL } from '../../../store/actions/commercialActions';
import { toggleAlertMessage } from '../../../store/action';

const TABS = ['programme', 'portfolio', 'worklist', 'customers', 'payouts'];
const ERRORS = {
  link_reason_required: 'Give a reason for the link.',
  not_direct_customer: 'Only direct Compare Your Footprint customers can be linked to an Ambassador.',
  bank_details_missing: 'This Ambassador has not saved bank details.',
  notice_not_supported: 'Notice can only be recorded on Ambassador or Partner Reseller Agreements.',
  forbidden: 'Platform Admin access is required.',
};

const BANK_STATUS_LABELS = {
  missing: 'Missing',
  pending_verification: 'Awaiting verification',
  verified: 'Verified',
};

const PAYOUT_STATUS_LABELS = {
  document_issued: 'Document issued',
  awaiting_funding: 'Awaiting funding',
  funding_requested: 'Funding requested',
  paid: 'Paid',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const money = (value, currency = 'GBP') =>
  value == null || value === ''
    ? '—'
    : new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(value));

const today = () => new Date().toISOString().slice(0, 10);

const selectProps = {
  classNamePrefix: 'cyf-select',
  isClearable: true,
  isSearchable: true,
  styles: adminToolbarSelect,
  menuPosition: 'fixed',
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
};

const statusBadgeVariant = (status) => {
  if (status === 'paid' || status === 'verified') return 'default';
  if (status === 'failed' || status === 'cancelled') return 'destructive';
  if (status === 'awaiting_funding' || status === 'pending_verification' || status === 'document_issued') return 'secondary';
  return 'outline';
};

const PortfolioTab = ({ rows, loading }) => {
  const history = useHistory();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        String(r.name || '').toLowerCase().includes(q) ||
        String(r.ambassadorOrgId || '').toLowerCase().includes(q) ||
        String(r.payoutCurrency || '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  if (loading) {
    return (
      <div className='flex items-center gap-2 text-muted-foreground py-8'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading portfolio…
      </div>
    );
  }

  if (!rows.length) {
    return (
      <p className='text-sm text-muted-foreground py-6'>
        No Ambassadors match the current Agreement and firm-status filters.
      </p>
    );
  }

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap gap-3 items-end'>
        <div className='min-w-[16rem] grow'>
          <Label htmlFor='portfolio-search'>Search</Label>
          <Input
            id='portfolio-search'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Name, org id, currency'
          />
        </div>
        <p className='text-xs text-muted-foreground pb-2'>
          {filtered.length} Ambassador{filtered.length === 1 ? '' : 's'}
        </p>
      </div>

      <div className='rounded-md border border-border overflow-x-auto'>
        <Table>
          <TableHeader>
            <TableRow className='border-border'>
              <TableHead>Ambassador</TableHead>
              <TableHead>Agreement signed</TableHead>
              <TableHead className='text-right'>Customers</TableHead>
              <TableHead className='text-right'>Paying</TableHead>
              <TableHead className='text-right'>Rev 12m</TableHead>
              <TableHead className='text-right'>Share lifetime</TableHead>
              <TableHead className='text-right'>YoY attr.</TableHead>
              <TableHead>Bank</TableHead>
              <TableHead>Minimum</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((row) => {
              const lifetime = (row.windows || []).find((w) => w.window === 'lifetime') || {};
              const last12 = (row.windows || []).find((w) => w.window === '12m') || {};
              const signed = formatAgreementSigned(row.agreementAcceptedAt);
              return (
                <TableRow
                  key={row.ambassadorOrgId}
                  className='border-border cursor-pointer hover:bg-muted/40'
                  onClick={() => history.push(`/admin/ambassadors/${row.ambassadorOrgId}`)}
                >
                  <TableCell className='font-medium text-foreground'>{row.name || row.ambassadorOrgId}</TableCell>
                  <TableCell>
                    {signed ? (
                      signed
                    ) : (
                      <Badge variant='secondary' className='text-muted-foreground font-normal'>
                        N/A
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className='text-right'>{row.referredCustomers ?? 0}</TableCell>
                  <TableCell className='text-right'>{row.payingReferredCustomers ?? 0}</TableCell>
                  <TableCell className='text-right'>{money(last12.attributedRevenueBase, row.baseCurrency)}</TableCell>
                  <TableCell className='text-right'>{money(lifetime.sharePayout, row.payoutCurrency)}</TableCell>
                  <TableCell className='text-right'>
                    {row.yoyAttributedPct == null ? '—' : `${row.yoyAttributedPct > 0 ? '+' : ''}${row.yoyAttributedPct}%`}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusBadgeVariant(row.bankDetailsStatus)}>
                      {BANK_STATUS_LABELS[row.bankDetailsStatus] || BANK_STATUS_LABELS.missing}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={row.minimumMet ? 'default' : 'secondary'}>
                      {row.minimumMet ? 'Met' : 'Not met'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push(`/admin/ambassadors/${row.ambassadorOrgId}`);
                      }}
                    >
                      Open overview
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const WorklistTab = ({ queue, loading }) => {
  const history = useHistory();

  if (loading) {
    return (
      <div className='flex items-center gap-2 text-muted-foreground py-8'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading worklist…
      </div>
    );
  }

  if (!(queue || []).length) {
    return <p className='text-sm text-muted-foreground py-6'>No Ambassadors need attention right now.</p>;
  }

  return (
    <div className='rounded-md border border-border overflow-x-auto'>
      <Table>
        <TableHeader>
          <TableRow className='border-border'>
            <TableHead>Ambassador</TableHead>
            <TableHead>Reasons</TableHead>
            <TableHead>Blocker</TableHead>
            <TableHead>Last touch</TableHead>
            <TableHead>Next follow-up</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {queue.map((row) => {
            const missing = (row.reasons || []).includes('missing_agreement');
            return (
              <TableRow key={row.ambassadorOrgId} className='border-border'>
                <TableCell className='font-medium'>{row.name}</TableCell>
                <TableCell>
                  <div className='flex flex-wrap gap-1'>
                    {(row.reasons || []).map((r) => (
                      <Badge
                        key={r}
                        variant={r === 'missing_agreement' ? 'destructive' : 'secondary'}
                        className='font-normal'
                      >
                        {reasonLabel(r)}
                      </Badge>
                    ))}
                  </div>
                  {missing ? (
                    <p className='text-xs text-muted-foreground mt-1'>Re-engage outside the platform.</p>
                  ) : null}
                </TableCell>
                <TableCell>{row.blocker || '—'}</TableCell>
                <TableCell>{row.lastTouchAt ? String(row.lastTouchAt).slice(0, 10) : '—'}</TableCell>
                <TableCell>{row.nextFollowUpAt || '—'}</TableCell>
                <TableCell>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => history.push(`/admin/ambassadors/${row.ambassadorOrgId}`)}
                  >
                    Open →
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const CustomersTab = ({
  orgOptions,
  ambassadorOptions,
  orgsById,
  pendingBank,
  onLink,
  onVerify,
  onSetDemo,
}) => {
  const [customer, setCustomer] = useState(null);
  const [ambassador, setAmbassador] = useState(null);
  const [reason, setReason] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today());
  const [busy, setBusy] = useState(false);
  const [demoOrg, setDemoOrg] = useState(null);

  const submitLink = async () => {
    setBusy(true);
    const ok = await onLink({
      customerOrgId: customer?.value,
      ambassadorOrgId: ambassador?.value,
      reason,
      effectiveFrom,
    });
    setBusy(false);
    if (ok) {
      setCustomer(null);
      setReason('');
    }
  };

  const demoCurrent = demoOrg ? !!orgsById[demoOrg.value]?.demoAccount : false;

  return (
    <div className='space-y-4'>
      <Card className='bg-card text-card-foreground'>
        <CardHeader>
          <CardTitle className='text-base'>Link a referred customer</CardTitle>
        </CardHeader>
        <CardContent className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          <div>
            <Label>Customer organisation</Label>
            <BrandSelect
              {...selectProps}
              options={orgOptions}
              value={customer}
              onChange={setCustomer}
              placeholder='Search organisations'
            />
          </div>
          <div>
            <Label>Ambassador</Label>
            <BrandSelect
              {...selectProps}
              options={ambassadorOptions}
              value={ambassador}
              onChange={setAmbassador}
              placeholder='Select Ambassador'
            />
          </div>
          <div>
            <Label htmlFor='link-reason'>Reason</Label>
            <Input
              id='link-reason'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder='e.g. Introduced at event'
            />
          </div>
          <div>
            <Label htmlFor='link-from'>Effective from</Label>
            <Input
              id='link-from'
              type='date'
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </div>
          <p className='md:col-span-2 text-xs text-muted-foreground'>
            A new link ends any current link for that customer the day before. Revenue collected before the effective
            date stays with the earlier Ambassador. Direct customers only.
          </p>
          <div className='md:col-span-2'>
            <Button onClick={submitLink} loading={busy} disabled={!customer || !ambassador || !reason.trim()}>
              Link customer
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className='bg-card text-card-foreground'>
        <CardHeader>
          <CardTitle className='text-base'>Needs attention — bank verification</CardTitle>
        </CardHeader>
        <CardContent className='space-y-2'>
          {pendingBank.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No Ambassadors awaiting bank verification.</p>
          ) : (
            pendingBank.map((row) => (
              <div
                key={row.ambassadorOrgId}
                className='flex flex-wrap items-center justify-between gap-2 border border-border rounded-md p-3 text-sm'
              >
                <span>
                  {row.name || row.ambassadorOrgId}
                  {row.bankDetails?.accountLast4 ? ` · ending ${row.bankDetails.accountLast4}` : ''}
                </span>
                <Button size='sm' onClick={() => onVerify(row.ambassadorOrgId)}>
                  Verify bank details
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card className='bg-card text-card-foreground'>
        <CardHeader>
          <CardTitle className='text-base'>Demo accounts</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <BrandSelect
            {...selectProps}
            options={orgOptions}
            value={demoOrg}
            onChange={setDemoOrg}
            placeholder='Search organisations'
          />
          {demoOrg && (
            <div className='flex items-center gap-3 text-sm'>
              <Switch checked={demoCurrent} onCheckedChange={(v) => onSetDemo(demoOrg.value, v)} />
              <span>
                {demoCurrent
                  ? 'Demo: excluded from Partner fees, Attributed Revenue and minimums'
                  : 'Not a demo account'}
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const PayoutsTab = ({
  cycles,
  loading,
  ambassadorOptions,
  ambassadorNames,
  onConfirmRecipient,
  onIssueDocument,
  onPreparePayout,
  onExport,
}) => {
  const [expanded, setExpanded] = useState({});
  const [exceptionsOpen, setExceptionsOpen] = useState(false);
  const [ambassador, setAmbassador] = useState(null);
  const [recipientId, setRecipientId] = useState('');
  const [settlementId, setSettlementId] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [month, setMonth] = useState('');
  const [exportPreview, setExportPreview] = useState(null);
  const [busy, setBusy] = useState(null);

  const run = async (key, fn) => {
    setBusy(key);
    const ok = await fn();
    setBusy(null);
    return ok;
  };

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className='space-y-4'>
      <p className='text-sm text-muted-foreground'>
        Month-end settlement and payout prepare run automatically. For UK/Malta, Wise is prepare-only: fund the prepared
        batch once from Your account in Wise. Status below tracks each cycle.
      </p>

      {loading ? (
        <div className='flex items-center gap-2 text-muted-foreground py-6'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Loading payouts…
        </div>
      ) : cycles.length === 0 ? (
        <p className='text-sm text-muted-foreground py-4'>No self-billing documents or payouts yet.</p>
      ) : (
        <div className='rounded-md border border-border overflow-x-auto'>
          <Table>
            <TableHeader>
              <TableRow className='border-border'>
                <TableHead className='w-8' />
                <TableHead>Period</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className='text-right'>Count</TableHead>
                <TableHead className='text-right'>Total</TableHead>
                <TableHead>Batch</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cycles.map((cycle) => {
                const open = !!expanded[cycle.id];
                return (
                  <React.Fragment key={cycle.id}>
                    <TableRow
                      className='border-border cursor-pointer hover:bg-muted/40'
                      onClick={() => toggle(cycle.id)}
                    >
                      <TableCell className='text-muted-foreground'>
                        {open ? <ChevronDown size={16} strokeWidth={1.5} /> : <ChevronRight size={16} strokeWidth={1.5} />}
                      </TableCell>
                      <TableCell className='font-medium'>{cycle.period || '—'}</TableCell>
                      <TableCell>{cycle.currency || '—'}</TableCell>
                      <TableCell>
                        <Badge variant={statusBadgeVariant(cycle.status)}>
                          {PAYOUT_STATUS_LABELS[cycle.status] || cycle.status}
                        </Badge>
                      </TableCell>
                      <TableCell className='text-right'>{cycle.count}</TableCell>
                      <TableCell className='text-right'>{money(cycle.total, cycle.currency || 'GBP')}</TableCell>
                      <TableCell className='text-xs text-muted-foreground font-mono'>
                        {cycle.batchGroupId ? String(cycle.batchGroupId).slice(0, 8) + '…' : '—'}
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow className='border-border bg-muted/20'>
                        <TableCell colSpan={7} className='p-4'>
                          <Table>
                            <TableHeader>
                              <TableRow className='border-border'>
                                <TableHead>Ambassador</TableHead>
                                <TableHead>Document</TableHead>
                                <TableHead className='text-right'>Amount</TableHead>
                                <TableHead>Transfer</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(cycle.lines || []).map((line) => (
                                <TableRow key={line.payoutId || line.documentId} className='border-border'>
                                  <TableCell>
                                    {ambassadorNames[line.ambassadorOrgId] || line.ambassadorOrgId}
                                  </TableCell>
                                  <TableCell className='font-mono text-xs'>
                                    {line.documentNumber || line.documentId}
                                  </TableCell>
                                  <TableCell className='text-right'>{money(line.amount, line.currency)}</TableCell>
                                  <TableCell className='font-mono text-xs'>{line.transferId || '—'}</TableCell>
                                  <TableCell>
                                    <Badge variant={statusBadgeVariant(line.status)}>
                                      {PAYOUT_STATUS_LABELS[line.status] || line.status}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Collapsible open={exceptionsOpen} onOpenChange={setExceptionsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant='outline' size='sm' className='gap-2'>
            {exceptionsOpen ? <ChevronDown size={16} strokeWidth={1.5} /> : <ChevronRight size={16} strokeWidth={1.5} />}
            Exceptions (manual issue / prepare / export)
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className='mt-3'>
          <Card className='bg-card text-card-foreground'>
            <CardHeader>
              <CardTitle className='text-base'>Ops exceptions</CardTitle>
            </CardHeader>
            <CardContent className='grid grid-cols-1 md:grid-cols-2 gap-4 text-sm'>
              <p className='md:col-span-2 text-xs text-muted-foreground'>
                Use only when a scheduled job missed a settlement or a single document needs a one-off prepare. Normal
                cycles appear in the table above.
              </p>
              <div className='space-y-2'>
                <Label>Ambassador</Label>
                <BrandSelect
                  {...selectProps}
                  options={ambassadorOptions}
                  value={ambassador}
                  onChange={setAmbassador}
                  placeholder='Select Ambassador'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='ops-month'>Export month (YYYY-MM)</Label>
                <Input id='ops-month' value={month} onChange={(e) => setMonth(e.target.value)} placeholder='2026-03' />
              </div>

              <div className='space-y-2 md:col-span-2 border-t border-border pt-3'>
                <p className='font-semibold'>Confirm payout recipient</p>
                <div className='flex flex-wrap gap-2 items-end'>
                  <div className='grow min-w-[12rem]'>
                    <Label htmlFor='ops-recipient'>Recipient id</Label>
                    <Input id='ops-recipient' value={recipientId} onChange={(e) => setRecipientId(e.target.value)} />
                  </div>
                  <Button
                    size='sm'
                    loading={busy === 'confirm'}
                    disabled={!ambassador || !recipientId}
                    onClick={() =>
                      run('confirm', async () => {
                        const ok = await onConfirmRecipient(ambassador.value, recipientId);
                        if (ok) setRecipientId('');
                        return ok;
                      })
                    }
                  >
                    Confirm recipient
                  </Button>
                </div>
              </div>

              <div className='space-y-2 border-t border-border pt-3'>
                <p className='font-semibold'>Issue settlement document</p>
                <Label htmlFor='ops-settlement'>Settlement id</Label>
                <Input id='ops-settlement' value={settlementId} onChange={(e) => setSettlementId(e.target.value)} />
                <Button
                  size='sm'
                  loading={busy === 'issue'}
                  disabled={!settlementId}
                  onClick={() =>
                    run('issue', async () => {
                      const ok = await onIssueDocument(settlementId);
                      if (ok) setSettlementId('');
                      return ok;
                    })
                  }
                >
                  Issue document
                </Button>
              </div>

              <div className='space-y-2 border-t border-border pt-3'>
                <p className='font-semibold'>Prepare Wise payout</p>
                <Label htmlFor='ops-doc'>Document id</Label>
                <Input id='ops-doc' value={documentId} onChange={(e) => setDocumentId(e.target.value)} />
                <Button
                  size='sm'
                  loading={busy === 'prepare'}
                  disabled={!documentId}
                  onClick={() =>
                    run('prepare', async () => {
                      const ok = await onPreparePayout(documentId);
                      if (ok) setDocumentId('');
                      return ok;
                    })
                  }
                >
                  Prepare payout (no fund)
                </Button>
              </div>

              <div className='space-y-2 md:col-span-2 border-t border-border pt-3'>
                <p className='font-semibold'>Finance export</p>
                <Button
                  size='sm'
                  variant='outline'
                  loading={busy === 'export'}
                  disabled={!ambassador}
                  onClick={() =>
                    run('export', async () => {
                      const data = await onExport(ambassador.value, month || undefined);
                      if (data) setExportPreview(data);
                      return !!data;
                    })
                  }
                >
                  Load export
                </Button>
                {exportPreview && (
                  <pre className='mt-2 max-h-48 overflow-auto rounded-md bg-muted p-2 text-xs'>
                    {JSON.stringify(exportPreview, null, 2)}
                  </pre>
                )}
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

/**
 * Platform Admin Ambassadors shell: Portfolio (R-6108), Customers (R-6109 / R-6106), Payouts (I-033).
 * Agreement creation stays under Sales Portal → New agreement.
 */
const ProgrammeTab = ({ data, loading, onOpenFirm }) => {
  if (loading) {
    return (
      <div className='flex items-center gap-2 text-muted-foreground py-8'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading programme…
      </div>
    );
  }
  const tlc = data?.tlcQueue || [];
  const quadrant = data?.quadrant;
  const revenueRows = data?.programmeAttributed || [];
  const revenueChart = {
    chart: { type: 'line', height: 220, backgroundColor: 'transparent' },
    title: { text: undefined },
    credits: { enabled: false },
    xAxis: { categories: revenueRows.map((r) => r.month) },
    yAxis: { title: { text: 'Attributed revenue' } },
    series: [{ name: 'Attributed (approx.)', data: revenueRows.map((r) => r.attributed) }],
    legend: { enabled: false },
  };
  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader>
          <CardTitle className='text-base'>Attributed revenue (programme)</CardTitle>
        </CardHeader>
        <CardContent>
          {revenueRows.length ? (
            <HighchartsReact highcharts={Highcharts} options={revenueChart} />
          ) : (
            <p className='text-sm text-muted-foreground py-6'>No attributed revenue series yet.</p>
          )}
          <p className='mt-2 text-xs text-muted-foreground'>
            Approximate from org signals until Stripe / fee history is wired.
          </p>
        </CardContent>
      </Card>
      <div className='grid md:grid-cols-2 gap-4'>
        <Card>
          <CardHeader>
            <CardTitle className='text-base'>TLC queue</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Firm</TableHead>
                  <TableHead>Flag</TableHead>
                  <TableHead>Why</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tlc.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className='text-muted-foreground'>
                      No TLC flags right now
                    </TableCell>
                  </TableRow>
                ) : (
                  tlc.map((r) => (
                    <TableRow
                      key={r.firmId || r.firmName}
                      className='cursor-pointer hover:bg-muted/50'
                      onClick={() => onOpenFirm?.(r.firmId)}>
                      <TableCell>{r.firmName}</TableCell>
                      <TableCell>
                        <Badge variant='outline'>{r.flag}</Badge>
                      </TableCell>
                      <TableCell className='text-muted-foreground'>{r.reason}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className='text-base'>Health quadrant</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='relative h-56 rounded border border-border bg-card'>
              <div className='absolute left-1/2 top-0 bottom-0 w-px bg-border' />
              <div className='absolute top-1/2 left-0 right-0 h-px bg-border' />
              <div className='absolute top-1 left-2 text-xs text-muted-foreground'>Leaky growth</div>
              <div className='absolute top-1 right-2 text-xs text-muted-foreground'>Champions</div>
              <div className='absolute bottom-6 left-2 text-xs text-muted-foreground'>At risk</div>
              <div className='absolute bottom-6 right-2 text-xs text-muted-foreground'>Stable plateau</div>
              {(quadrant?.points || []).map((p) => {
                const maxSize = Math.max(...(quadrant?.points || []).map((q) => q.size || 1), 1);
                const size = Math.max(14, 10 + (18 * (p.size || 1)) / maxSize);
                const x = Math.min(95, Math.max(5, Number(p.x) || 50));
                const y = Math.min(95, Math.max(5, 50 + (Number(p.y) || 0) / 2));
                return (
                  <button
                    key={p.id}
                    type='button'
                    title={`${p.name} · retention ${p.x} · growth ${p.y}`}
                    aria-label={p.name}
                    className='absolute z-10 rounded-full hover:scale-110 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground'
                    style={{
                      width: size,
                      height: size,
                      left: `${x}%`,
                      bottom: `${y}%`,
                      marginLeft: -size / 2,
                      marginBottom: -size / 2,
                      // Solid dark fill — bg-primary/opacity often fails on CSS-var themes and leaves hollow rings
                      backgroundColor: '#1B4332',
                      boxShadow: '0 0 0 2px #fff, 0 1px 4px rgba(0,0,0,.35)',
                    }}
                    onClick={() => onOpenFirm?.(p.id)}
                  />
                );
              })}
              <div className='absolute bottom-1 left-1/2 -translate-x-1/2 text-xs text-muted-foreground'>Retention →</div>
            </div>
            <p className='mt-2 text-xs text-muted-foreground'>
              Hover for name; click opens firm Overview. Firm list lives under Portfolio.
            </p>
          </CardContent>
        </Card>
      </div>
      <ClientMixPanel mix={data?.clientMix} title='Referred-customer mix (programme)' />
      {data?.metricsNote ? <p className='text-xs text-muted-foreground'>{data.metricsNote}</p> : null}
    </div>
  );
};

const AmbassadorsAdmin = ({
  adminOrgs,
  getAdminOrganisations,
  getAmbassadorReport,
  getAmbassadorEngagementQueue,
  getPartnersAmbassadorsProgramme,
  listAmbassadorPayouts,
  linkAmbassadorCustomer,
  verifyAmbassadorBankDetails,
  setDemoFlag,
  confirmPayoutRecipient,
  issueSettlementDocument,
  prepareDocumentPayout,
  exportSelfBilling,
  showAlert,
}) => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const tabFromUrl = searchParams.get('tab');
  const activeTab = TABS.includes(tabFromUrl) ? tabFromUrl : 'programme';
  const listFilters = useMemo(() => readListFilters(searchParams), [searchParams]);

  const [report, setReport] = useState(null);
  const [programme, setProgramme] = useState(null);
  const [queue, setQueue] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [loadingReport, setLoadingReport] = useState(true);
  const [loadingProgramme, setLoadingProgramme] = useState(true);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [loadingPayouts, setLoadingPayouts] = useState(false);

  const replaceSearch = useCallback(
    (mutate) => {
      const params = new URLSearchParams(location.search);
      mutate(params);
      const qs = params.toString();
      history.replace({ pathname: '/admin/partners/ambassadors', search: qs ? `?${qs}` : '' });
    },
    [history, location.search]
  );

  const setTab = (value) => {
    replaceSearch((params) => {
      if (value === 'programme') params.delete('tab');
      else params.set('tab', value);
    });
  };

  const setListFilters = ({ agreement, status }) => {
    replaceSearch((params) => {
      if (!agreement || agreement === 'all') params.delete('agreement');
      else params.set('agreement', agreement);
      if (!status || status === 'active') params.delete('status');
      else params.set('status', status);
    });
  };

  const reloadReport = useCallback(async () => {
    setLoadingReport(true);
    const res = await getAmbassadorReport(listFilters);
    setLoadingReport(false);
    if (res.ok) setReport(res.data);
    else showAlert(true, ERRORS[res.error] || res.error, 'error', 6000);
  }, [getAmbassadorReport, listFilters, showAlert]);

  const reloadProgramme = useCallback(async () => {
    setLoadingProgramme(true);
    try {
      const res = await getPartnersAmbassadorsProgramme(listFilters);
      if (res?.ok) setProgramme(res.data);
      else showAlert(true, res?.error || 'Failed to load Ambassadors programme', 'error', 6000);
    } catch (e) {
      showAlert(true, e?.message || 'Failed to load Ambassadors programme', 'error', 6000);
    } finally {
      setLoadingProgramme(false);
    }
  }, [getPartnersAmbassadorsProgramme, listFilters, showAlert]);

  const reloadPayouts = useCallback(async () => {
    setLoadingPayouts(true);
    const res = await listAmbassadorPayouts();
    setLoadingPayouts(false);
    if (res.ok) setCycles(res.data?.cycles || []);
    else showAlert(true, ERRORS[res.error] || res.error, 'error', 6000);
  }, [listAmbassadorPayouts, showAlert]);

  const reloadQueue = useCallback(async () => {
    setLoadingQueue(true);
    const res = await getAmbassadorEngagementQueue({ status: listFilters.status });
    setLoadingQueue(false);
    if (res.ok) setQueue(res.data?.rows || []);
    else showAlert(true, ERRORS[res.error] || res.error, 'error', 6000);
  }, [getAmbassadorEngagementQueue, listFilters.status, showAlert]);

  useEffect(() => {
    reloadProgramme();
    reloadReport();
    getAdminOrganisations();
  }, [reloadProgramme, reloadReport, getAdminOrganisations]);

  useEffect(() => {
    if (activeTab === 'payouts') reloadPayouts();
    if (activeTab === 'worklist') reloadQueue();
  }, [activeTab, reloadPayouts, reloadQueue]);
  const orgOptions = useMemo(
    () => (adminOrgs || []).map((o) => ({ value: o.id, label: o.name || o.id })),
    [adminOrgs]
  );
  const orgsById = useMemo(() => Object.fromEntries((adminOrgs || []).map((o) => [o.id, o])), [adminOrgs]);
  const rows = report?.rows || [];
  const ambassadorOptions = useMemo(
    () => rows.map((r) => ({ value: r.ambassadorOrgId, label: r.name || r.ambassadorOrgId })),
    [rows]
  );
  const ambassadorNames = useMemo(
    () => Object.fromEntries(rows.map((r) => [r.ambassadorOrgId, r.name || r.ambassadorOrgId])),
    [rows]
  );
  const pendingBank = useMemo(
    () => rows.filter((r) => r.bankDetailsStatus === 'pending_verification'),
    [rows]
  );

  const run = async (promise, success) => {
    const res = await promise;
    if (!res.ok) {
      showAlert(true, ERRORS[res.error] || res.error, 'error', 6000);
      return false;
    }
    showAlert(true, success, 'success', 4000);
    reloadReport();
    if (activeTab === 'payouts') reloadPayouts();
    return true;
  };

  return (
    <div className='p-6 space-y-4'>
      <Breadcrumb
        items={[
          { label: 'Partners', link: '/admin/partners' },
          { label: 'Ambassadors', link: '/admin/partners/ambassadors' },
        ]}
      />
      <div>
        <h1 className='text-xl font-semibold text-foreground'>Ambassadors</h1>
        <p className='text-sm text-muted-foreground mt-1'>
          Programme health, portfolio, referred-customer ops and payouts. Firms without a signed Agreement appear
          unless filtered out; Worklist lists missing agreements and engagement blockers. Create agreements under
          Sales Portal.
        </p>
      </div>

      {(activeTab === 'programme' || activeTab === 'portfolio') && (
        <PartnersFilterBar
          agreement={listFilters.agreement}
          status={listFilters.status}
          onChange={setListFilters}
        />
      )}

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value='programme'>Programme</TabsTrigger>
          <TabsTrigger value='portfolio'>Portfolio</TabsTrigger>
          <TabsTrigger value='worklist'>Worklist</TabsTrigger>
          <TabsTrigger value='customers'>Customers</TabsTrigger>
          <TabsTrigger value='payouts'>Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value='programme'>
          <ProgrammeTab
            data={programme}
            loading={loadingProgramme}
            onOpenFirm={(id) => id && history.push(`/admin/ambassadors/${id}`)}
          />
        </TabsContent>

        <TabsContent value='portfolio'>
          <PortfolioTab rows={rows} loading={loadingReport} />
        </TabsContent>
        <TabsContent value='worklist'>
          <WorklistTab queue={queue} loading={loadingQueue} />
        </TabsContent>

        <TabsContent value='customers'>
          <CustomersTab
            orgOptions={orgOptions}
            ambassadorOptions={ambassadorOptions}
            orgsById={orgsById}
            pendingBank={pendingBank}
            onLink={(body) => run(linkAmbassadorCustomer(body), 'Customer linked.')}
            onVerify={(id) => run(verifyAmbassadorBankDetails(id), 'Bank details verified.')}
            onSetDemo={async (id, demo) => {
              if (await run(setDemoFlag(id, demo), demo ? 'Marked as demo.' : 'Demo flag cleared.')) {
                getAdminOrganisations();
              }
            }}
          />
        </TabsContent>

        <TabsContent value='payouts'>
          <PayoutsTab
            cycles={cycles}
            loading={loadingPayouts}
            ambassadorOptions={ambassadorOptions}
            ambassadorNames={ambassadorNames}
            onConfirmRecipient={(orgId, recipientId) =>
              run(confirmPayoutRecipient(orgId, recipientId), 'Payout recipient confirmed.')
            }
            onIssueDocument={(settlementId) =>
              run(issueSettlementDocument(settlementId, { entityCountry: 'GB' }), 'Self-billing document issued.')
            }
            onPreparePayout={(documentId) => run(prepareDocumentPayout(documentId), 'Payout prepared (no fund).')}
            onExport={async (orgId, month) => {
              const res = await exportSelfBilling(orgId, month);
              if (!res.ok) {
                showAlert(true, ERRORS[res.error] || res.error, 'error', 6000);
                return null;
              }
              showAlert(true, 'Export loaded.', 'success', 3000);
              return res.data?.export || res.data;
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const mapStateToProps = (state) => ({ adminOrgs: state?.admin?.organisations });

const mapDispatchToProps = (dispatch) => ({
  getAdminOrganisations: () => dispatch(ADMINACTIONS.getAdminOrganisations({ includeAll: true })),
  getAmbassadorReport: (filters) => dispatch(COMMERCIAL.getAmbassadorReport(filters)),
  getAmbassadorEngagementQueue: (filters) => dispatch(COMMERCIAL.getAmbassadorEngagementQueue(filters)),
  getPartnersAmbassadorsProgramme: (filters) => dispatch(COMMERCIAL.getPartnersAmbassadorsProgramme(filters)),
  listAmbassadorPayouts: () => dispatch(COMMERCIAL.listAmbassadorPayouts()),
  linkAmbassadorCustomer: (body) => dispatch(COMMERCIAL.linkAmbassadorCustomer(body)),
  verifyAmbassadorBankDetails: (id) => dispatch(COMMERCIAL.verifyAmbassadorBankDetails(id)),
  setDemoFlag: (id, demo) => dispatch(COMMERCIAL.setDemoFlag(id, demo)),
  confirmPayoutRecipient: (orgId, recipientId) => dispatch(COMMERCIAL.confirmPayoutRecipient(orgId, recipientId)),
  issueSettlementDocument: (settlementId, body) => dispatch(COMMERCIAL.issueSettlementDocument(settlementId, body)),
  prepareDocumentPayout: (documentId) => dispatch(COMMERCIAL.prepareDocumentPayout(documentId)),
  exportSelfBilling: (orgId, month) => dispatch(COMMERCIAL.exportSelfBilling(orgId, month)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AmbassadorsAdmin);
